import { CalendarDays, ShoppingBag, SlidersVertical, Truck } from "lucide-react";
import { Icon, Spotlight, type SpotlightTile } from "@cntrl/ui";
import { useDos } from "../../app/hooks/useDayOfShow.js";
import { useFinancials } from "../../app/hooks/useMoney.js";
import { useTravel } from "../../app/hooks/useTravel.js";
import { useMerchRollup } from "../../app/hooks/useMerchRollup.js";
import { useSpotlightPhoto } from "../../app/hooks/useSpotlightPhoto.js";
import { formatMoneyCents } from "../../app/hooks/inboxFormat.js";
import { callTimes, countdownLabel, clock, daysUntil, fullDate, useNextShows, whereLabel } from "./showDay.js";

/**
 * The per-seat dashboard heroes (the reference's `.spotlight` family). One chassis — `@cntrl/ui`'s
 * {@link Spotlight}: eyebrow + headline + optional delta pill + exactly three stat tiles — and a
 * different ANSWER per seat, because what matters at 4pm on a show day depends entirely on the job.
 * Which seat gets which hero is decided server-side (`nav.dashboard.main`); these just render.
 *
 * Every tile is null-degraded to `—` by the primitive: a hero NEVER fabricates a figure (born-blank +
 * leak-test), so an unsettled show shows a dash rather than `$0`.
 */

// The show-day resolution — dates, call times and the venue line — lives in `showDay.ts`, shared with the
// mobile home's show-day hero (ART-126). The call-time rule in particular is not re-implementable safely:
// load-in and stage are per ACT, and resolving them without the lineup slot puts a changeover under
// "Showtime".

// ---- band member ------------------------------------------------------------------------------

/**
 * The band member's hero: their next show and its call times. (The "YTD with band" own-pay tile was removed
 * 2026-09-28 with the settlement_payouts table it read.)
 */
export function MemberSpotlight() {
  const { shows, isLoading } = useNextShows();
  const next = shows[0];
  const dos = useDos(next?.showId ?? "");
  const photo = useSpotlightPhoto();

  // Call times live on the run-of-show, not on the show row, and are per ACT — see `callTimes`.
  const { loadIn, stage } = callTimes(dos.data);

  const days = daysUntil(next?.date);
  const tiles: SpotlightTile[] = [
    { label: "Load-in", value: clock(loadIn) },
    { label: "Showtime", value: clock(stage) },
  ];

  return (
    <Spotlight
      eyebrow={<><Icon icon={CalendarDays} size={16} /> Next show · {countdownLabel(days)}</>}
      title={whereLabel(next)}
      subtitle={fullDate(next?.date) ?? "Once a date is confirmed it shows up here with your call times."}
      loading={isLoading}
      tiles={tiles}
      photoUrl={photo}
    />
  );
}

// ---- tour manager -----------------------------------------------------------------------------

/**
 * The tour manager's hero: today's/the next show's OPS — what it pays, where everyone sleeps, and what
 * is next. The guarantee is band money, so it renders only for a money-team seat; a TM holds that by
 * seat policy, and a seat that doesn't simply sees `—` (the read 403s and the tile degrades).
 */
export function TmSpotlight() {
  const { shows, isLoading } = useNextShows();
  const next = shows[0];
  const after = shows[1];
  const financials = useFinancials(next?.showId, Boolean(next?.showId));
  const travel = useTravel(next?.showId ?? "");
  const photo = useSpotlightPhoto();

  const lodging = travel.data?.items?.find((r) => r.kind === "hotel");
  const days = daysUntil(next?.date);
  const tiles: SpotlightTile[] = [
    {
      label: "Guarantee",
      value: financials.data?.data?.guaranteeCents != null
        ? formatMoneyCents(financials.data.data.guaranteeCents)
        : null,
    },
    { label: "Tonight's lodging", value: lodging?.propertyName ?? null, small: true },
    { label: "Next venue", value: after?.venueName ?? null, small: true },
  ];

  return (
    <Spotlight
      eyebrow={<><Icon icon={Truck} size={16} /> {days === 0 ? "Show day · TODAY" : `Next show · ${countdownLabel(days)}`}</>}
      title={whereLabel(next)}
      subtitle={fullDate(next?.date) ?? "Your next confirmed date and its numbers land here."}
      loading={isLoading}
      tiles={tiles}
      photoUrl={photo}
    />
  );
}

// ---- production trio --------------------------------------------------------------------------

/**
 * The production seat's hero (FOH / lighting / tech): the next date's WORK CLOCK. When the truck opens,
 * when the room is theirs to check, and when it stops being theirs — doors is the hard deadline every
 * production task is scheduled backwards from.
 *
 * The reference leads with FOH console / Stage / Capacity read from a crowd-sourced venue-spec directory.
 * That directory has no source in this backend, and no CNTRL app has ever shipped a surface that writes one
 * (`.design-specs/dashboard.audit.md`), so all three tiles would be permanent em-dashes — which is exactly
 * what the legacy rendered. Venue knowledge here is name + city; the CLOCK is band-entered and real, so it
 * carries the hero instead. The venue tiles return with the directory.
 */
export function ProductionSpotlight() {
  const { shows, isLoading } = useNextShows();
  const next = shows[0];
  const dos = useDos(next?.showId ?? "");
  const photo = useSpotlightPhoto();

  const { loadIn, soundcheck, doors } = callTimes(dos.data);

  const days = daysUntil(next?.date);
  const tiles: SpotlightTile[] = [
    { label: "Load-in", value: clock(loadIn) },
    { label: "Soundcheck", value: clock(soundcheck) },
    { label: "Doors", value: clock(doors) },
  ];

  return (
    <Spotlight
      eyebrow={
        <>
          <Icon icon={SlidersVertical} size={16} />{" "}
          {days === 0 ? "Show day · TODAY" : `Next show · ${countdownLabel(days)}`}
        </>
      }
      title={whereLabel(next)}
      subtitle={fullDate(next?.date) ?? "Your next confirmed date and its call times land here."}
      loading={isLoading}
      tiles={tiles}
      photoUrl={photo}
    />
  );
}

// ---- merch manager ----------------------------------------------------------------------------

/**
 * The merch manager's hero: what is selling. Today / 30-day / top seller off the store rollup, with the
 * 30-day total as the delta-free headline figure (the rollup carries no prior-period baseline, so we
 * never render a delta pill we cannot substantiate).
 */
export function MerchSpotlight() {
  const q = useMerchRollup();
  const photo = useSpotlightPhoto();
  const rollup = q.data?.rollup ?? null;
  const connected = q.data?.connected === true;
  const top = rollup?.bestSellers?.[0] ?? null;

  const tiles: SpotlightTile[] = [
    { label: "Today", value: rollup?.salesTodayCents != null ? formatMoneyCents(rollup.salesTodayCents) : null },
    { label: "30-day", value: rollup?.sales30dCents != null ? formatMoneyCents(rollup.sales30dCents) : null },
    { label: "Top seller", value: top?.title ?? null, small: true },
  ];

  return (
    <Spotlight
      eyebrow={<><Icon icon={ShoppingBag} size={16} /> Top movers · last 30 days</>}
      title="What's selling"
      subtitle={
        connected
          ? "Your store's last 30 days. Reorder the table for tonight based on what's hot."
          : "Connect your store and tonight's numbers show up here."
      }
      loading={q.isLoading}
      tiles={tiles}
      photoUrl={photo}
    />
  );
}
